/**
 * Decline codes come from company.opsDesk.declineReasons.
 * A note may sit beside the code. A string that is not an enabled code is refused.
 */
import type { AssignmentReasonCode } from "./assignment";
import { normalizeDeclineReasonId } from "./opsDeskSettings";
import type { OpsDeclineReason } from "./types";

export function enabledDeclineReasons(reasons: readonly OpsDeclineReason[] | null | undefined): OpsDeclineReason[] {
  return (reasons ?? []).filter((row) => row.enabled !== false && row.id && row.label);
}

export function resolveDeclineReason(
  reasons: readonly OpsDeclineReason[] | null | undefined,
  raw: unknown,
): { ok: true; id: string; label: string } | { ok: false; error: string } {
  const id = normalizeDeclineReasonId(raw);
  if (!id) return { ok: false, error: "Choose an enabled decline reason from company settings." };
  const hit = enabledDeclineReasons(reasons).find((row) => row.id === id);
  if (!hit) return { ok: false, error: "That decline reason is not an enabled company setting." };
  return { ok: true, id: hit.id, label: hit.label };
}

/** Match an id or an exact label. Free text that is neither returns null. */
export function matchDeclineReason(reasons: readonly OpsDeclineReason[] | null | undefined, raw: unknown): string | null {
  const enabled = enabledDeclineReasons(reasons);
  const id = normalizeDeclineReasonId(raw);
  const byId = id ? enabled.find((row) => row.id === id) : undefined;
  if (byId) return byId.id;
  const text = String(raw ?? "").trim().toLowerCase();
  if (!text) return null;
  const byLabel = enabled.find((row) => row.label.trim().toLowerCase() === text);
  return byLabel?.id ?? null;
}

export function fallbackDeclineReason(reasons: readonly OpsDeclineReason[] | null | undefined): string | null {
  return matchDeclineReason(reasons, "other") ?? enabledDeclineReasons(reasons)[0]?.id ?? null;
}

const ASSIGNMENT_TO_DECLINE: Record<AssignmentReasonCode, string> = {
  cannot_fly_time: "crew",
  weather: "weather",
  aircraft: "aircraft",
  personal: "other",
  defer: "schedule",
  other: "other",
};

export function declineIdForAssignment(
  reasons: readonly OpsDeclineReason[] | null | undefined,
  code: AssignmentReasonCode,
): string | null {
  return matchDeclineReason(reasons, ASSIGNMENT_TO_DECLINE[code]);
}

const CANCEL_TO_DECLINE: Record<string, string> = {
  scheduling: "schedule",
  operational: "aircraft",
  conflict: "schedule",
  ferry_sandwich: "schedule",
  other: "other",
};

export function declineIdForCancel(reasons: readonly OpsDeclineReason[] | null | undefined, code: string | null | undefined): string | null {
  const key = String(code ?? "").trim().toLowerCase();
  return matchDeclineReason(reasons, CANCEL_TO_DECLINE[key] ?? key);
}

export function assignmentCodeForDecline(id: string): AssignmentReasonCode {
  if (id === "weather") return "weather";
  if (id === "aircraft") return "aircraft";
  if (id === "crew") return "cannot_fly_time";
  if (id === "schedule") return "defer";
  return "other";
}
